import { planMonthEstimate } from '../plan/planMonthEstimate';

/**
 * KAN-196 — the plans a company can pick in the chooser. Plan names, prices and
 * seats come ONLY from the catalog, delivered by `get_my_billing_status().plans`
 * (ordered by sort_order). No plans from the server -> no list (the caller shows
 * a load error); nothing here ever invents a price. Custom is never sold via Stripe.
 */
export const SELLABLE_CODES = Object.freeze(['starter', 'growth', 'pro']);

export function resolveBillablePlans(status) {
  const src = Array.isArray(status?.plans) ? status.plans : [];
  return src.filter((p) => SELLABLE_CODES.includes(String(p?.code)));
}

/** Catalog name for any plan code (incl. a non-sellable current plan); '' when unknown. */
export function billablePlanName(status, code) {
  const src = Array.isArray(status?.plans) ? status.plans : [];
  return src.find((p) => p?.code === code)?.name || '';
}

/** A plan the server says does not fit is shown but cannot be picked. Missing flag = fits. */
export const planFits = (plan) => plan?.fits !== false;

/**
 * Preselect: a `preferred` plan code when it fits (the cancel flow's "switch to {plan}"), else the current plan when it fits, else the cheapest plan that fits
 * (catalog order is price order), else null.
 */
export function defaultPlanSelection(plans, currentCode, preferred = null) {
  const pre = preferred ? plans.find((p) => p.code === preferred) : null;
  if (pre && planFits(pre)) return pre.code;
  const cur = plans.find((p) => p.code === currentCode);
  if (cur && planFits(cur)) return cur.code;
  return plans.find(planFits)?.code ?? null;
}

/** Plan estimates for N pools + the cheapest plan that "fits" (null when pools is unknown). */
export function estimatePlans(plans, pools) {
  if (!Number.isFinite(pools)) return { estimates: {}, cheapest: null };
  const estimates = {};
  let cheapest = null;
  plans.forEach((p) => {
    const e = planMonthEstimate(p, pools);
    estimates[p.code] = e;
    const roomy = p.included_pools == null || pools <= Number(p.included_pools);
    if (e != null && roomy && (cheapest === null || e < estimates[cheapest])) cheapest = p.code;
  });
  return { estimates, cheapest };
}

/**
 * Round 3 — the cancel flow's "Or switch to {plan}" offer: the nearest sellable plan BELOW the current one in
 * price order (catalog order) that the server says fits. Null when the current plan is not sellable, is the
 * cheapest, or nothing cheaper fits — then the offer is not shown at all.
 */
export function cheaperFittingPlan(status) {
  const plans = resolveBillablePlans(status);
  const at = plans.findIndex((p) => p.code === status?.planCode);
  if (at <= 0) return null;
  for (let i = at - 1; i >= 0; i -= 1) if (planFits(plans[i])) return plans[i];
  return null;
}
